<template>
    <div class="b2b-checkout-confirmation">
        <div class="page-content">
            <div class="confirmation_container">
                <div class="confirmation-container-wrapper">
                    <CheckmarkCircleIcon alt="Order confirmation icon" />
                    <h2 class="confirmation_container-title headline-l">Thank You For Your {{ isQuote ? 'Quote Request' : 'Order' }}{{webOrderIds.length > 1 ? 's' : ''}}!</h2>
                    <p v-if="webOrderIds.length > 0" class="confirmation_container-id paragraph-feature">{{ webOrderIds.map(webOrderId => `Web Confirmation #${webOrderId}`).join(", ") }}</p>
                    <p v-if="props.order.code" class="confirmation_container-id paragraph-feature">{{`Order #${props.order.code}` }}</p>
                    <div class="confirmation_container-text">
                        <p>Here are the details of your {{ isQuote ? 'quote' : 'order' }}. You will also find the details of your {{ isQuote ? 'quote' : 'order' }} in your dealer account.
                         A copy of your {{ isQuote ? 'quote' : 'order' }} confirmation will be sent to {{ props.order.billing_email }}.
                        </p>
                        <p v-if="!isQuote"><span>Please note:</span> if we cannot ship your order in its entirety, we will notify you after the order is processed.</p>
                    </div>
                    <div class="confirmation_container-actions">
                        <a v-if="orders.length < 10" href="#" @click.prevent="getConfirmation" class="confirmation_container-print">
                            <PrintIcon alt="Print icon" />
                            <h3 class="title-s">Print Your {{ isQuote ? 'Quote' : 'Order' }}</h3>
                        </a>
                        <RothcoCta v-if="props.order.order_source == 'erp'" class="secondary" aria-label="View or download order CSV" @click="downloadCsv" :inertia="false">
                            Download Order CSV
                        </RothcoCta>
                        <RothcoCta v-if="props.order.invoice_id && props.order.billing_email" @click.prevent="exportInvoice" class="secondary" aria-label="Export invoice via email">
                            Email Invoice
                        </RothcoCta>
                        <RothcoCta :href="route('index.quickOrdering')" class="primary" aria-label="Start a new order">
                            Start a New Order
                        </RothcoCta>
                    </div>
                </div>
            </div>
            <hr />
            <!-- B2B ORDER SUMMARY-->
            <div class="b2b-order-summary">
                <div class="b2b-card">
                    <div class="content">
                        <div class="content-head">
                            <div class="headline-s title">Summary</div>
                            <a href="#" @click.prevent="scrollToItemsGrid()">View Items In {{ isQuote ? 'Quote' : 'Order' }}</a>
                        </div>
                        <div class="row">
                            <div class="paragraph-default col-title">Quantity</div>
                            <div class="quantity-container">
                                <div class="paragraph-default">{{ totalStyles }} Styles</div>
                                <div class="paragraph-default bar-styling">|</div>
                                <div class="paragraph-default">{{ totalSkus }} SKUs</div>
                                <div class="paragraph-default bar-styling">|</div>
                                <div class="paragraph-default">{{ totalQuantity }} Quantity</div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="paragraph-default col-title">Approx. Wgt</div>
                            <div class="paragraph-default">{{ approxWeight.toFixed(2) }}lbs</div>
                        </div>
                        <div class="row">
                            <div class="paragraph-default col-title">Merchandise</div>
                            <div class="paragraph-default">${{ merchandiseCost }}</div>
                        </div>
                        <div  v-if="parseFloat(shippingCost) > 0" class="row">
                            <div class="paragraph-default col-title">Shipping</div>
                            <div class="paragraph-default">${{ shippingCost }}</div>
                        </div>
                        <div v-if="props.order.drop_ship == 1" class="row">
                            <div class="paragraph-default col-title">Dropship Fee</div>
                            <div class="paragraph-default"><i>${{ formatPrice(props.order.dropship_fee) }}</i></div>
                        </div>
                        <!-- <div class="row">
                            <div class="paragraph-default col-title">Discounts</div>
                            <div class="paragraph-default text-rothcoRed"><i>-${{ totalDiscounts }}</i></div>
                        </div> -->
                        <div class="row">
                            <div class="total-value-title"><bold>Total</bold></div>
                            <div class="total-value">${{ orderTotal }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- B2B SHIPPING & BILLING-->
            <div class="b2b-shipping-billing-summary">
                <shipping-confirm v-model="shipping" :allowEdit="false" isSuccess />
                <billing-confirm v-model="billing" :allowEdit="false" :paymentMethods="paymentMethods"/>
            </div>
            <!-- B2B ITEMS IN ORDER-->
            <div v-for="order of orders" class="b2b-items-in-order-summary" id="tableContainer" :index="order" :key="order.order_id">
                <div class="title-container">
                    <div class="title-l">Items in Order {{ orders.length == 1 ? '' : `#${order}` }}</div>
                    <div class="content text-rothcoText">
                        <div class="paragraph-default">{{ getTotal('totalStyles', order) }} Styles</div>
                        <div class="paragraph-default">|</div>
                        <div class="paragraph-default">{{ getTotal('totalSkus', order) }} SKUs</div>
                        <div class="paragraph-default">|</div>
                        <div class="paragraph-default">{{ getTotal('totalQuantity', order) }} Quantity</div>
                        <div class="paragraph-default">|</div>
                        <div class="paragraph-default">Approx. Wgt: {{ getTotal('approxWeight', order).toFixed(2) }}lbs</div>
                    </div>
                </div>

                <div class="b2b-items-in-order-table">
                    <rothco-table
                        class="table"
                        key="table"
                        :content="gridItems.get(order)"
                        :visible-fields="visibleFields"
                        dataKey="list_item_id"
                        :pt="{
                            bodyRow: {
                                class: 'table-item table-item-3-col'
                            },
                            rowExpansion: {
                                class: 'error-row'
                            }
                        }">

                        <template v-for="(item, index) in gridItems.get(order)" :key="`item_image_${index}`" v-slot:[`item_image_${index}`]="slotProps">
                            <div class="img-background">
                                <img :src="item.data.image" :srcset="item.data.image_srcset" :sizes="item.data.image_sizes" alt="Product name">
                            </div>
                        </template>

                        <template v-for="(item, index) in gridItems.get(order)" :key="`item_name_${index}`" v-slot:[`item_name_${index}`]="slotProps">
                            <a v-if="slotProps.header.show_only == 'mobile'" target="_blank" class="card-title" :href="route('productDetail.show', [item.data.seo_name])">
                                {{ item.data.item_name }}
                            </a>
                            <a v-else class="table-name paragraph-default" target="_blank" :href="route('productDetail.show', [item.data.seo_name])">
                                {{ item.data.item_name }}
                            </a>
                        </template>

                        <template v-for="(item, index) in gridItems.get(order)" :key="`variants_badges_${index}`" v-slot:[`variants_badges_${index}`]="slotProps">
                            <div class="badge-wrapper">
                                <p v-if="Object.keys(item?.data?.variants_badges ?? {}).length === 0" class="paragraph-default badge" style="visibility: hidden">No Variants</p>
                                <p v-for="(variantValue,i) in (item.data.variants_badges ?? {})" :key="i" class="paragraph-default badge">{{ variantValue }}</p>
                            </div>
                        </template>

                        <template v-for="(item, index) in gridItems.get(order)" :key="`price_${index}`" v-slot:[`price_${index}`]="slotProps">
                            <span>
                                ${{ parseFloat(item.data.price).toFixed(2) }}
                            </span>
                        </template>

                        <template v-for="(item, index) in gridItems.get(order)" :key="`cost_${index}`" v-slot:[`cost_${index}`]="slotProps">
                            <span>
                                ${{(formatTotalPrice(item.data.price, item.data.quantity))}}
                            </span>
                        </template>
                    </rothco-table>
                </div>
            </div>

            <!-- B2B ORDER TOTAL-->
            <div class="b2b-order-total-summary">
                <section class="summary-section">
                    <div class="summary-container">
                        <dl>
                            <dt class="paragraph-default">Merchandise</dt>
                            <dd class="paragraph-default">${{ merchandiseCost }}</dd>
                        </dl>
                        <!-- <dl>
                            <dt class="paragraph-default">Discounts</dt>
                            <dd class="paragraph-default text-rothcoRed font-italic">${{ discountAmount }}</dd>
                        </dl> -->
                        <dl v-if="props.order.drop_ship == 1">
                            <dt class="paragraph-default">Dropship Fee</dt>
                            <dd class="paragraph-default">${{ formatPrice(props.order.dropship_fee) }}</dd>
                        </dl>
                        <dl  v-if="parseFloat(shippingCost) > 0">
                            <dt class="paragraph-default">Shipping</dt>
                            <dd class="paragraph-default">${{ shippingCost }}</dd>
                        </dl>
                        <dl>
                            <dt class="paragraph-default">Taxes</dt>
                            <dd :class="{'font-italic': isTaxExempt || order.tax == 0, 'paragraph-default': true}">{{isTaxExempt ? 'Tax Exempt' : order.tax == 0 ? 'Processing' : `$${formatPrice(order.tax)}`  }}</dd>
                        </dl>
                        <dl class="order-total">
                            <dt class="title-l">{{ isQuote ? 'Quote' : 'Order' }} Total</dt>
                            <dd class="title-l">${{ orderTotal }}</dd>
                        </dl>
                        <div v-if="!hasShippingCost && order.order_source != 'erp'" class="paragraph-details text-rothcoText shipping-not-included-text">
                            Does not include shipping.
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
    <!-- <SalesRestrictionsWarning />
      <OutOfStockWarning /> -->
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, toRefs, nextTick, inject } from "vue";
    import { usePage, router } from "@inertiajs/vue3";
    // import { route } from "ziggy-js";
    import { useFetch } from "@/helpers/composables/useFetch";
    import ShippingConfirm from "@/components/ShippingConfirm.vue";
    import BillingConfirm from "@/components/BillingConfirm.vue";
    import RothcoTable from "@/components/RothcoTable.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import { useLists } from "@/helpers/composables/useList";
    import { useIntelliSuggest } from "@/helpers/composables/intellisuggest";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import { formatPrice, formatTotalPrice } from "@/helpers/composables/usePriceFormatting";
    import NProgress from "nprogress";
    import CheckmarkCircleIcon from "@icons/checkmark-circle.svg";
    import PrintIcon from "@icons/print-icon.svg";
    import useGoogleManager from "@/helpers/composables/google";
    import { useActiveCampaign } from "@/helpers/composables/activeCampaign";
    import { comingSoonImages } from "@/helpers/composables/comingSoonImages";
    import {
        productUploadImageResponsiveFromFilename,
        PRODUCT_IMAGE_SIZES_LIST_ROW
    } from "@/helpers/productUploadImageUrl";

    function orderLineImageAttrs(item: { item_variation?: { primary_image?: { image_filename?: string } } }) {
        const fn = item?.item_variation?.primary_image?.image_filename;
        if (!fn) {
            return {
                image: comingSoonImages().small,
                image_srcset: undefined as string | undefined,
                image_sizes: undefined as string | undefined
            };
        }
        const pack = productUploadImageResponsiveFromFilename(
            fn,
            PRODUCT_IMAGE_SIZES_LIST_ROW
        );
        return {
            image: pack.src,
            image_srcset: pack.srcSet,
            image_sizes: pack.sizes
        };
    }
    import { useMetaConversionAPI } from "@/helpers/composables/useMetaConversionAPI";
    import { useQuantcast } from '@/helpers/composables/quantcast';
    let { purchaseEvent } = useQuantcast();
    let settings = useSettingsStore();
    let { user } = storeToRefs(settings);
    let { setOverlayMessage, setNotificationMessage } = settings;
    const props = defineProps({
        order: {
            type: Object,
            default() {
                return {
                    total: 0,
                    count: 0,
                    billing_name: "",
                    billing_address: "",
                    billing_address_2: "",
                    billing_city: "",
                    billing_state: "",
                    billing_country: "",
                    billing_zip: "",
                    billing_phone: "",
                    billing_email: "",
                    billing_comments: "",
                    /**
                     * make shipping fields
                     */
                    shipping_name: "",
                    shipping_address: "",
                    shipping_address_2: "",
                    shipping_city: "",
                    shipping_state: "",
                    shipping_country: "",
                    shipping_zip: "",
                    shipping_phone: "",
                    shipping_email: "",
                    shipping_comments: "",
                    collectAccount: null,
                    drop_ship: 0,
                    dropship_fee: 0,

                    discount: 0,
                    order_items: [],
                    code: "" // ERP Code
                };
            }
        },
        paymentMethods: {
            type: Array,
            default: () => {
                return [];
            }
        },
        additionalOrders: {
            type: Array,
            default: () => {
                return [];
            }
        },
        isTaxExempt: {
            type: Boolean,
            default: false
        },
        isSuccessPage: {
            type: Boolean,
            default: false
        }
    });
    let { order } = toRefs(props);

    let orders = computed(() => {
        return [order.value.order_id, ...props.additionalOrders.map((order) => order.order_id)];
    });

    const webOrderIds = computed(()=>{
        return [order.value.web_order_id, ...props.additionalOrders.map((order) => order.web_order_id)].filter((webOrderId)=>(!!webOrderId));
    })

    let items = computed(() => {
        return [...order.value.order_items.map((item) => {
                const imgs = orderLineImageAttrs(item);
                return {
                    ...item,
                    price: item.price,
                    quantity: item.quantity,
                    variation: item?.item_variation ?? {},
                    image: imgs.image,
                    image_srcset: imgs.image_srcset,
                    image_sizes: imgs.image_sizes
                };
            }),
            ...props.additionalOrders.reduce((acc, order) => {
                return [
                    ...acc,
                    ...order.order_items.map((item) => {
                        const imgs = orderLineImageAttrs(item);
                        return {
                            ...item,
                            price: item.price,
                            quantity: item.quantity,
                            variation: item?.item_variation ?? {},
                            image: imgs.image,
                            image_srcset: imgs.image_srcset,
                            image_sizes: imgs.image_sizes
                        };
                    })
                ];
            }, [])
        ];
    });

    let billing = computed(() => {
        return {
            billing_credit_card_id: order.value.billing_credit_card_id,
            billing_po_number: order.value.po_number,
            billing_payment_method: order.value.billing_payment_method,
            order_type: order.value.order_type,
            billing_email: order.value.billing_email,
            billing_alternate_email: order.value.billing_alternate_email,
            billing_comments: order.value.billing_comments,
            creditCard: order.value?.creditCard ?? {},
        };
    });

    let shipping = computed(() => {
        return {
            shipping_name: order.value.shipping_name,
            shipping_in_care_of: order.value.shipping_in_care_of,
            shipping_must_ship_date: order.value.must_ship_date,
            shipping_method: order.value.shipping_method,
            shipping_address: order.value.shipping_address,
            shipping_address_2: order.value.shipping_address_2,
            shipping_city: order.value.shipping_city,
            shipping_state: order.value.shipping_state,
            shipping_country: order.value.shipping_country,
            shipping_zip: order.value.shipping_zip,
            shipping_phone: order.value.shipping_phone,
            dropship_fee: order.value.dropship_fee,
            drop_ship: order.value.drop_ship,
            ship_blind: order.value.ship_blind,
            shipping_email: order.value.shipping_email,
            shipping_comments: order.value.shipping_comments,
            shipping_final_destination_state:
                order.value.shipping_final_destination_state,
            shipping_final_destination_country:
                order.value.shipping_final_destination_country,
            freight_payment_term: order.value.freight_payment_term,
            collect_no_id: order.value.collect_no_id,
            collectAccount: order.value.collectAccount,
            tracking_number: order.value.tracking_number,
            tracking_url: order.value.tracking_url,
        };
    });

    const isQuote = computed(()=>{
        return order.value.order_type == 'Quote';
    })

    let shippingCost = computed(() => {
        return (+(order.value.shipping_charge ?? 0)).toFixed(2);
    });

    let hasShippingCost = computed(() => {
        return parseFloat(shippingCost.value) > 0;
    });

    const merchandiseCost = computed(() => {
        return formatPrice(
            items.value
                .reduce((total, item) => {
                    let subTotalForItem = formatTotalPrice(item.price, item.quantity);
                    return total + +subTotalForItem;
                }, 0) ?? 0
        );
    });

    const discountCost = computed(() => {
        return order.value.discount?.toFixed?.(2) ?? 0;
    });

    const totalQuantity = computed(() => {
        return items.value.reduce((total, item) => {
            return total + item.quantity;
        }, 0);
    });

    const totalSkus = computed(() => 
        new Set(items.value.map(item => item.variation.item_variation_id)).size
    );

    const totalStyles = computed(() => 
        new Set(items.value.map(item => item.variation?.rothcoItem?.rothco_item_no)).size
    );

    const approxWeight = computed(() => {
        return items.value.reduce((weight, item) => {
            return weight + +item.variation.weight * item.quantity;
        }, 0);
    });

    let gridItems = computed(() => {
            let map = new Map();
            // make two keys for order ids
            for(let order of orders.value){
                map.set(order, []);
            }
            return items.value.reduce((acc, item) => {
              let order = map.get(item.order_id);
              if(!order){
                    order = [];   
              }
                order.push({
                    data: {
                        ...item,
                        item_name: item.variation.item?.name,
                        item_no: item.variation.rothcoItem?.rothco_item_no,
                        seo_name: item.variation.item?.seo_name,
                        variants_text: Object.values(item?.variation?.text_description ?? {}).join(" - "),
                        variants_badges: item.variation.text_description,
                        quantity: item.quantity
                    },
                    list_item_id: item.list_item_id
                });
                acc.set(item.order_id, order);
                return acc;
            }, map);

    });

    function getTotal(field, order){
        let gridItemsForOrder = gridItems.value.get(order);

        switch(field){
            case 'totalStyles':
                return new Set(gridItemsForOrder.map(item => item.data.variation?.rothcoItem?.rothco_item_no)).size
            case 'totalSkus':
                return new Set(gridItemsForOrder.map(item => item.data.variation.item_variation_id)).size
            case 'totalQuantity':
                return gridItemsForOrder.reduce((total, item) => {
                    return total + item.data.quantity;
                }, 0);
            case 'approxWeight':
                return gridItemsForOrder.reduce((weight, item) => {
                    return weight + +item.data.variation.weight * item.data.quantity;
                }, 0);
           
        }
        return gridItems.value[order].reduce((total, item) => {
            return total + item.data[field];
        }, 0);
    }

    function scrollToItemsGrid() {
        document.querySelector('.table-item')?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }
    let route = inject("route");
    let { updateListItem } = useLists({ router, route });


    const visibleFields = computed(()=>{
        return [
            {
                label: "",
                field: "item_image",
                is_custom: true,
                show_only: 'desktop',
                pt: {
                    headerCell: {},
                    bodyCell: { class: 'desktop-only desktop-image' }
                }
            },
            {
                label: "Item Name",
                field: "item_name",
                is_custom: true,
                show_only: 'mobile',
                pt: {
                    headerCell: { class: 'text-left' },
                    bodyCell: { class: 'mob-w-100 no-title', 'data-label': "Item" }
                }
            },
            {
                label: "Item Name",
                field: "item_name",
                is_custom: true,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'text-left' },
                    bodyCell: { 'data-label': "Item" }
                }
            },
            {
                label: "Item #",
                field: "item_no",
                is_custom: false,
                pt: {
                    headerCell: { class: 'text-left' },
                    bodyCell: { class: 'mob-w-25 align-top', 'data-label': "Item" },
                }
            },
            {
                label: "Variant",
                field: "variants_text",
                is_custom: false,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'text-left' },
                    bodyCell: { class: 'mob-w-75' }
                }
            },
            {
                label: "Variant",
                field: "variants_badges",
                is_custom: true,
                show_only: 'mobile',
                pt: {
                    bodyCell: { class: 'mob-w-75', 'data-label': "Variation" }
                }
            },
            {
                label: "Unit Price",
                field: "price",
                is_custom: true,
                pt: {
                    headerCell: { class: 'text-right' },
                    bodyCell: { class: 'mob-w-25 desk-text-right', 'data-label': "Unit Price" }
                }
            },
            {
                label: "Qty",
                field: "quantity",
                is_custom: false,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'text-right' },
                    bodyCell: (state)=>{
                        console.log(state)
                        return { 
                            class: 'mob-w-25 desk-text-right desktop-only', 
                            'data-label': "Qty",
                            id: `quantity_${state.instance.rowData.list_item_id}_desktop`  
                        }
                    }
                }
            },
            {
                label: "Cost",
                field: "cost",
                is_custom: true,
                show_only: 'mobile',
                pt: {
                    headerCell: { class: 'text-right' },
                    bodyCell: { 
                        class: 'mob-w-25 mob-text-left desk-text-right',
                        'data-label': "Cost" 
                    }
                }
            },
            {
                label: "Qty",
                field: "quantity",
                is_custom: false,
                show_only: 'mobile',
                pt: {
                    bodyCell: (state)=>{
                        return { 
                            class: 'mob-w-25 mobile-only', 
                            'data-label': "Qty",
                            id: `quantity_${state.instance.rowData.list_item_id}_mobile`
                        }
                    }
                }
            },
            {
                label: "Cost",
                field: "cost",
                is_custom: true,
                show_only: 'desktop',
                pt: {
                    headerCell: { class: 'text-right' },
                    bodyCell: { 
                        class: 'mob-w-25 mob-text-left desk-text-right',
                        'data-label': "Cost" 
                    }
                }
            },
        ]
    })

    let timeout;


    let { execute: getConfirmation } = useFetch(route('endpoint.order_confirmation', { [orders.value.length > 1 ? 'orderIds' : 'orderId']: orders.value.length > 1 ? orders.value.join(",") : order.value.order_id, order_source: order.value.order_source }), {
        immediate: false,
        beforeFetch: () => {
            NProgress.start();
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                setOverlayMessage("Printing your order. Please wait...")
            }, 5000);
        },
        afterFetch: (response) => {
            NProgress.done();
            clearTimeout(timeout);
            setOverlayMessage(null);
            let blob = response.data;
            let url = window.URL.createObjectURL(blob);
            let iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            iframe.onload = function() {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
            };  
        },
        onFetchError: (err)=>{
            if(err?.error?.name !== 'AbortError'){
                clearTimeout(timeout);
            }
        }
    }).get().blob();

     let { execute: exportInvoice } = useFetch(route('export.invoice.web'), {
        immediate: false,
        beforeFetch: () => {
            NProgress.start();
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                setOverlayMessage("Processing your request. Please wait...")
            }, 5000);
        },
        afterFetch: (ctx) => {
            NProgress.done();
            clearTimeout(timeout);
            setOverlayMessage(null);
            if(ctx.response.ok){
                setNotificationMessage({
                    message: `The invoice will be sent to ${order.value.billing_email} shortly`,
                    type: 'success'
                });
            }  
        },
        onFetchError: (err)=>{
            if(err?.error?.name !== 'AbortError'){
                clearTimeout(timeout);
                setNotificationMessage({
                    message: `You don't have permission to export this invoice`,
                    type: 'error'
                });
            }
        }
    }).post({
        order_id: order.value.order_id
    });

    let { sale } = useIntelliSuggest({
        siteId: "xxkp6b",
        shopperId: user.value.user_id,
    })

    let orderTotal = computed(() => {
        return  formatPrice(props.additionalOrders.reduce((acc, additionalOrder) => {
            if(Number.isNaN(additionalOrder.total)){
                return acc;
            }
            return acc + (+additionalOrder.total);
        }, Number.isNaN(order.value.total) ? 0 :  +order.value.total));
    });

    let { gaPurchaseTransaction, gtmPurchaseEvent, googleConversion } = useGoogleManager();
    let { trackConversion } = useActiveCampaign();
    let { metaPurchase } = useMetaConversionAPI();

    function recordAnalytics(order){
        trackConversion(order.total);
        sale({
            items: order.order_items.map(item => {
                return {
                    sku: item.item_variation.item?.product_line_code,
                    childSku: item.item_variation?.rothcoItem.rothco_item_no,
                    qty: item.quantity,
                    price: item.price
                } }),
            orderId: order.order_id,
            total: order.total,
            city: order.shipping_city,
            state: order.shipping_state,
            country: order.shipping_country,     
        })
        
        gtmPurchaseEvent({
            ...order,
            items: order.order_items.map(order_item=>{
                return {
                    ...order_item,
                    variation: order_item.item_variation,
                }
            }),
        }, 'B2B Website');

        gaPurchaseTransaction({
            ...order,
            items: order.order_items.map(order_item=>{
                return {
                    ...order_item,
                    variation: order_item.item_variation,
                }
            }),
        });

        googleConversion(order);

        const subtotal = +order.subtotal || 0;
        const shipping = +order.shipping_charge || 0;
        const dropship = +order.dropship_fee || 0;
        const total = +order.total || 0;
        
        metaPurchase({
            order_id: order.order_id,
            total: total,
            subtotal: subtotal,
            shipping: shipping,
            dropship: dropship,
            items: order.order_items.map(order_item => {
                return {
                    ...order_item,
                    variation: order_item.item_variation,
                }
            }),
        });
        purchaseEvent({
            email: user.value.email,
            orderId: order.order_id,
            total: order.total,
        });
    }

    onMounted(()=>{

        if(props.isSuccessPage){

            recordAnalytics(order.value);
            for(let additionalOrder of props.additionalOrders){
                recordAnalytics(additionalOrder);
            }

        }
        
    })


        const reportUrl = computed(() => {
            const params = {
                report_options: {
                    type: 'order-details',
                    file_type: 'csv',
                },
                with: [
                    'sku',
                    // 'orderStatus', 'paymentTerms', 'billingCountry', 'shippingCountry', 'finalDestinationCountry'
                ].join(','),
                limit: 1000,
                filter: {
                    order_id: props.order.order_id,
                }
            }
            return route('endpoint.report.download', params);
        })
        
        const { data: reportData, execute: downloadCsv } = useFetch(reportUrl, {
        beforeFetch(ctx){
            NProgress.start();
            // ctx.headers['Accept'] = 'application/json';
            return ctx;
        },
        afterFetch(ctx){
            const url = window.URL.createObjectURL(ctx.data); // Create a download link from blob
            const a = document.createElement('a');
            a.href = url;
            
            a.download = `Order_${props.order.code}_Details.csv`; // Specify the filename for download
            document.body.appendChild(a);
            a.click();
            a.remove();
            return ctx;
        },
        onFetchError(err){
            if(err?.error?.name !== 'AbortError'){
                NProgress.done();
            }
        },
        immediate: false,
    }).get().blob();
</script>
